<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CarListingCard from './components/CarListingCard.vue';
import KeywordComboBox from './components/KeywordComboBox.vue';
import SocialPublishDialog from './components/SocialPublishDialog.vue';
import { catalogItemLabel, catalogItemKeywords } from './helpers';

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();

const STATUSES = ['draft', 'active', 'archived', 'sold'];

const statusFilter = ref('');
const makeFilter = ref('');
const searchTerm = ref('');

const uiFlags = useMapGetter('wgptCarListings/getUIFlags');
const carListings = useMapGetter('wgptCarListings/getCarListings');
const meta = useMapGetter('wgptCarListings/getMeta');
const catalog = useMapGetter('wgptCarCatalog/getCatalog');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const isSocialEnabled = computed(() =>
  isFeatureEnabled.value(accountId.value, FEATURE_FLAGS.WGPT_SOCIAL_MEDIA_POSTS)
);
const socialDialogRef = ref(null);

const isFetching = computed(() => uiFlags.value.isFetching);
const isFetchingMore = computed(() => uiFlags.value.isFetchingMore);

const makeOptions = computed(() =>
  catalog.value.makes.map(make => ({
    value: make.id,
    label: catalogItemLabel(make, locale.value),
    keywords: catalogItemKeywords(make),
  }))
);

const statusCounts = computed(() => meta.value.status_counts || {});
const totalCount = computed(() =>
  Object.values(statusCounts.value).reduce((sum, count) => sum + count, 0)
);

const hasListings = computed(() => carListings.value.length > 0);
const hasActiveFilters = computed(
  () => statusFilter.value || makeFilter.value || searchTerm.value.trim()
);
const hasMore = computed(
  () => meta.value.current_page < meta.value.total_pages
);

const fetchListings = (page = 1) => {
  store.dispatch('wgptCarListings/get', {
    status: statusFilter.value || undefined,
    make_id: makeFilter.value || undefined,
    q: searchTerm.value.trim() || undefined,
    page,
    append: page > 1,
  });
};

const loadMore = () => fetchListings(meta.value.current_page + 1);

watch([statusFilter, makeFilter], () => fetchListings(1));
watch(
  searchTerm,
  useDebounceFn(() => fetchListings(1), 300)
);

onMounted(() => fetchListings(1));

const handleNew = () => {
  router.push({ name: 'wgpt_car_listings_new' });
};

const handleSettings = () => {
  router.push({ name: 'wgpt_car_listings_settings' });
};

const handleShowroom = () => {
  router.push({ name: 'wgpt_car_listings_showroom_settings' });
};

const handleEdit = listing => {
  router.push({
    name: 'wgpt_car_listings_editor',
    params: { listingId: listing.id },
  });
};

const handleDelete = async listing => {
  try {
    await store.dispatch('wgptCarListings/delete', listing.id);
    useAlert(t('WGPT_CAR_LISTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('WGPT_CAR_LISTINGS.DELETE.API.ERROR_MESSAGE'));
  }
};

const handleShare = listing => socialDialogRef.value?.open(listing);

const clearFilters = () => {
  statusFilter.value = '';
  makeFilter.value = '';
  searchTerm.value = '';
};
</script>

<template>
  <div class="flex flex-col flex-1 w-full max-w-6xl gap-6 p-6 mx-auto">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h1 class="text-2xl font-medium text-n-slate-12">
          {{ t('WGPT_CAR_LISTINGS.HEADER_TITLE') }}
        </h1>
        <span class="text-sm text-n-slate-11">
          {{ t('WGPT_CAR_LISTINGS.SUBTITLE', { count: totalCount }) }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('WGPT_CAR_SHOWROOM.SHOWROOM_NAV_LABEL')"
          icon="i-lucide-globe"
          variant="faded"
          color="slate"
          size="sm"
          @click="handleShowroom"
        />
        <Button
          :label="t('WGPT_CAR_LISTINGS.SETTINGS.NAV_LABEL')"
          icon="i-lucide-user-round-cog"
          variant="faded"
          color="slate"
          size="sm"
          @click="handleSettings"
        />
        <Button
          :label="t('WGPT_CAR_LISTINGS.NEW_LISTING')"
          icon="i-lucide-plus"
          size="sm"
          @click="handleNew"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <div class="flex items-center gap-1 p-1 rounded-lg bg-n-alpha-1">
        <button
          class="px-3 py-1 text-sm rounded-md transition-colors"
          :class="
            statusFilter === ''
              ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="statusFilter = ''"
        >
          {{ t('WGPT_CAR_LISTINGS.FILTERS.ALL') }}
          <span class="text-xs text-n-slate-10">{{ totalCount }}</span>
        </button>
        <button
          v-for="status in STATUSES"
          :key="status"
          class="px-3 py-1 text-sm rounded-md transition-colors"
          :class="
            statusFilter === status
              ? 'bg-n-surface-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="statusFilter = status"
        >
          {{ t(`WGPT_CAR_LISTINGS.STATUS.${status}`) }}
          <span class="text-xs text-n-slate-10">
            {{ statusCounts[status] || 0 }}
          </span>
        </button>
      </div>
      <div class="w-48">
        <KeywordComboBox
          v-model="makeFilter"
          :options="makeOptions"
          :placeholder="t('WGPT_CAR_LISTINGS.FILTERS.MAKE_PLACEHOLDER')"
        />
      </div>
      <div class="flex-1 min-w-48">
        <Input
          v-model="searchTerm"
          :placeholder="t('WGPT_CAR_LISTINGS.FILTERS.SEARCH_PLACEHOLDER')"
        />
      </div>
    </div>

    <div
      v-if="isFetching"
      class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
    >
      <div
        v-for="skeleton in 6"
        :key="skeleton"
        class="flex flex-col overflow-hidden border rounded-xl border-n-weak"
      >
        <div class="animate-pulse aspect-video bg-n-alpha-2" />
        <div class="flex flex-col gap-2 p-3">
          <div class="w-2/3 h-4 rounded animate-pulse bg-n-alpha-2" />
          <div class="w-1/2 h-3 rounded animate-pulse bg-n-alpha-2" />
        </div>
      </div>
    </div>

    <div
      v-else-if="!hasListings && !hasActiveFilters"
      class="flex flex-col items-center justify-center gap-3 pt-14 text-center"
    >
      <span class="text-4xl i-lucide-car text-n-slate-9" />
      <span class="text-lg font-medium text-n-slate-12">
        {{ t('WGPT_CAR_LISTINGS.EMPTY_STATE.TITLE') }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{ t('WGPT_CAR_LISTINGS.EMPTY_STATE.SUBTITLE') }}
      </span>
      <Button
        :label="t('WGPT_CAR_LISTINGS.NEW_LISTING')"
        icon="i-lucide-plus"
        size="sm"
        @click="handleNew"
      />
    </div>

    <div
      v-else-if="!hasListings"
      class="flex flex-col items-center justify-center gap-3 pt-14 text-center"
    >
      <span class="text-4xl i-lucide-search-x text-n-slate-9" />
      <span class="text-lg font-medium text-n-slate-12">
        {{ t('WGPT_CAR_LISTINGS.EMPTY_FILTERED.TITLE') }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{ t('WGPT_CAR_LISTINGS.EMPTY_FILTERED.SUBTITLE') }}
      </span>
      <Button
        :label="t('WGPT_CAR_LISTINGS.EMPTY_FILTERED.CLEAR')"
        variant="faded"
        color="slate"
        size="sm"
        @click="clearFilters"
      />
    </div>

    <template v-else>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <CarListingCard
          v-for="listing in carListings"
          :key="listing.id"
          :listing="listing"
          :share-enabled="isSocialEnabled"
          @edit="handleEdit(listing)"
          @delete="handleDelete(listing)"
          @share="handleShare(listing)"
        />
      </div>
      <div v-if="hasMore" class="flex justify-center pb-8">
        <Button
          :label="t('WGPT_CAR_LISTINGS.LOAD_MORE')"
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="isFetchingMore"
          @click="loadMore"
        />
      </div>
    </template>
    <SocialPublishDialog ref="socialDialogRef" />
  </div>
</template>
